import type { StyleLibrary } from "schemas/generated/project";

import React from "react";

import { useFetchGlobalsCssForWebContainer } from "@editor/hooks/designLibrary/useFetchGlobalsCssForWebContainer";
import { trpc } from "@editor/utils/trpc";

import isEqual from "lodash-es/isEqual";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

type DesignLibraryView =
  | "summary"
  | "colors"
  | "typography"
  | "presets"
  | "fontPresets"
  | "customFonts";

export function useDesignLibrary(projectId: string | null | undefined) {
  const updateGlobalsCss = useFetchGlobalsCssForWebContainer();

  const [currentView, setCurrentView] =
    React.useState<DesignLibraryView>("summary");
  const [previousView, setPreviousView] =
    React.useState<DesignLibraryView>("summary");

  const { data: styleLibraryData } = trpc.styles.getStyleLibrary.useQuery(
    { projectId: projectId ?? "" },
    { enabled: Boolean(projectId) },
  );

  const { mutateAsync: updateStyleLibrary } =
    trpc.styles.createOrUpdateStyleLibrary.useMutation();

  const trpcUtils = trpc.useUtils();

  const [currentStyleLibrary, setCurrentStyleLibrary] = useOverridableState(
    styleLibraryData?.styleLibrary ?? null,
  );

  const hasUnsavedChanges = !isEqual(
    currentStyleLibrary,
    styleLibraryData?.styleLibrary ?? null,
  );

  const handleStyleLibraryChange = async (
    updatedStyleLibrary: StyleLibrary,
  ) => {
    if (!projectId) {
      return;
    }

    setCurrentStyleLibrary(updatedStyleLibrary);
    void updateGlobalsCss(updatedStyleLibrary);
  };

  const handleDiscard = async () => {
    if (!projectId) {
      return;
    }

    await trpcUtils.styles.getStyleLibrary.invalidate({ projectId });

    const originalStyleLibrary = styleLibraryData?.styleLibrary;
    if (originalStyleLibrary) {
      setCurrentStyleLibrary(originalStyleLibrary);
      void updateGlobalsCss(originalStyleLibrary);
    }
  };

  const handleSave = async () => {
    if (!projectId || !currentStyleLibrary) {
      return;
    }

    await updateStyleLibrary({
      projectId,
      styleLibrary: currentStyleLibrary,
    });

    await trpcUtils.styles.getStyleLibrary.invalidate({
      projectId: projectId ?? "",
    });

    await updateGlobalsCss(currentStyleLibrary);
  };

  const handleCustomFontsClick = () => {
    setPreviousView(currentView);
    setCurrentView("customFonts");
  };

  const handleCustomFontsBack = () => {
    setCurrentView(previousView);
  };

  return {
    // State
    currentView,
    setCurrentView,
    previousView,
    setPreviousView,
    currentStyleLibrary,
    setCurrentStyleLibrary,
    hasUnsavedChanges,
    styleLibraryData,

    // Handlers
    handleStyleLibraryChange,
    handleDiscard,
    handleSave,
    handleCustomFontsClick,
    handleCustomFontsBack,
  };
}
